import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'

import { AppLanguage } from '~/types/locale'
import { useAppSelector } from '~/redux/hooks'
import { isAuthenticated, selectUserIsSchoolTeacher } from '~/auth/slices/auth.slice'
import ROUTES from '~/shared/constants/routes.const'
import { getPanelType } from '~/utils'
import useCurrentUser from '~/hooks/useCurrentUser'
import { UserRole } from '~/types/user-info-types'
import { selectShowAllGames, selectShowAllLearningTools } from '~/redux/slices/menu'

import { useGetGradePageRouteForLink } from '~/dashboard/hooks/useGetGradePageRoute'
import userConfig from '~/config/app'

import { useGetCurrentGradeQuery } from '~/curriculum/apis/curriculum.api'
import { useGetUserChosenCurriculumCountryCode } from '~/curriculum/hooks/useGetCurriculumCountryCode'
import { getTestGenerationStatus } from '~/tests/utils/testUtils'
import { CurriculumCountryCodes } from '~/curriculum/constants/curriculum.const'
import useHasPurchaseTutoringPackages from '~/panels/hooks/useHasPurchaseTutoringPackages'

import { MenuCategoryType } from '../types/category.types'
import { MenuCategoryKeys } from '../constants/category.const'

import useHandleItemClick from './useGetHandlerItemClick'

export interface MenuCategories {
  learningTools: MenuCategoryType
  games: MenuCategoryType
  profile?: MenuCategoryType
}

export default function useGetMenuCategories(): MenuCategories {
  const allSubCategories = useGetAllSubCategories()
  const user = useCurrentUser()
  const panelType = getPanelType(user?.role)

  const authenticated = useAppSelector(isAuthenticated)
  const isSchoolTeacher = useAppSelector(selectUserIsSchoolTeacher)
  const countryCode = useGetUserChosenCurriculumCountryCode()
  const { data: currentGradeData } = useGetCurrentGradeQuery(undefined, { skip: !authenticated })
  const grade = currentGradeData?.grade
  const { isAllowed } = getTestGenerationStatus(grade ?? 0, countryCode as CurriculumCountryCodes)

  const showAllLearningTools = useAppSelector(selectShowAllLearningTools)
  const showAllGames = useAppSelector(selectShowAllGames)
  const hasPurchasedTutoringPackages = useHasPurchaseTutoringPackages()
  const router = useRouter()
  const isNO = router.locale === AppLanguage.NO

  const learningTools: MenuCategoryType = {
    ...allSubCategories[MenuCategoryKeys.LEARNING_TOOLS],
    subCategories: [
      allSubCategories[MenuCategoryKeys.CURRICULUM],
      ...(isAllowed ? [allSubCategories[MenuCategoryKeys.TEST_PREP]] : []),
      ...(!userConfig.isMobileApp && isNO && !userConfig.isSchoolPlatform
        ? [allSubCategories[MenuCategoryKeys.TOD]]
        : []),
      ...(showAllLearningTools
        ? [
            allSubCategories[MenuCategoryKeys.ENCYCLOPEDIA],
            allSubCategories[MenuCategoryKeys.STUDY_TIPS],
            allSubCategories[MenuCategoryKeys.PEN_N_PAPER_EXERCISE],
            allSubCategories[MenuCategoryKeys.EXCEL_GEOGEBRA],
            // isNO alone leaves the entry live on /no in the school build, where /exams 404s.
            ...(isNO && !userConfig.isSchoolPlatform ? [allSubCategories[MenuCategoryKeys.EXAMS]] : []),
          ]
        : []),
    ],
  }

  const games: MenuCategoryType = {
    ...allSubCategories[MenuCategoryKeys.GAMES],
    subCategories: [
      allSubCategories[MenuCategoryKeys.MULT_MASTER],
      allSubCategories[MenuCategoryKeys.JUNIOR_MATH],
      ...(showAllGames
        ? [
            allSubCategories[MenuCategoryKeys.TREASURE_TRAIL],
            allSubCategories[MenuCategoryKeys.WIND_SURFER],
            allSubCategories[MenuCategoryKeys.STACK_N_LOAD],
          ]
        : []),
    ],
  }

  if (!authenticated) {
    return { learningTools, games }
  }

  const isTutorOrAdmin = panelType === UserRole.TUTOR || panelType === UserRole.ADMIN
  // Students only see Student Panel after buying a tutoring package; tutors/admins always keep it.
  const homeAndStudentPanel = isTutorOrAdmin
    ? [allSubCategories[MenuCategoryKeys.HOME], allSubCategories[MenuCategoryKeys.STUDENT_PANEL]]
    : [
        ...(hasPurchasedTutoringPackages ? [allSubCategories[MenuCategoryKeys.STUDENT_PANEL]] : []),
        allSubCategories[MenuCategoryKeys.HOME],
      ]

  const profile: MenuCategoryType = {
    ...allSubCategories[MenuCategoryKeys.PROFILE],
    subCategories: [
      // The way back out of "Student mode": the avatar menu sends a teacher to the learner /dashboard,
      // and nothing else in this layout links to the panel they came from. First in the list because
      // it is the teacher's home, not one of their learner surfaces.
      //
      // Gated on the role as well as the build flag — `isSchoolPlatform` is true for the whole school
      // deploy, so the flag alone would show pupils an entry into a panel that 403s.
      ...(userConfig.isSchoolPlatform && isSchoolTeacher ? [allSubCategories[MenuCategoryKeys.TEACHER_PANEL]] : []),
      allSubCategories[MenuCategoryKeys.MASTERY_CERTIFICATES],
      // Student Panel and Tutor Panel are Tutor-on-Demand surfaces, so the school platform keeps only Home.
      ...(userConfig.isSchoolPlatform ? [allSubCategories[MenuCategoryKeys.HOME]] : homeAndStudentPanel),
      allSubCategories[MenuCategoryKeys.PROGRESS],
      ...(isTutorOrAdmin && !userConfig.isSchoolPlatform ? [allSubCategories[MenuCategoryKeys.TUTOR_PANEL]] : []),
      ...(panelType === UserRole.ADMIN || user?.hasPermissions ? [allSubCategories[MenuCategoryKeys.ADMIN_PANEL]] : []),
    ],
  }

  return { learningTools, games, profile }
}

export function useGetAllSubCategories(): { [key: string]: MenuCategoryType } {
  const authenticated = useAppSelector(isAuthenticated)
  const { t } = useTranslation(['home', 'imageAlt', 'menu'])
  const handleItemClick = useHandleItemClick()

  return {
    [MenuCategoryKeys.LEARNING_TOOLS]: {
      key: MenuCategoryKeys.LEARNING_TOOLS,
      title: t('menu:learningTools'),
    },
    [MenuCategoryKeys.GAMES]: {
      key: MenuCategoryKeys.GAMES,
      title: t('home:products.gamesTitle'),
    },
    [MenuCategoryKeys.PROFILE]: {
      key: MenuCategoryKeys.PROFILE,
      title: t('menu:profile'),
    },
    [MenuCategoryKeys.CURRICULUM]: {
      image: 'icons/menu-curriculum.png',
      imageHeight: 32,
      imageWidth: 32,
      key: MenuCategoryKeys.CURRICULUM,
      url: useGetGradePageRouteForLink(),
      title: t('menu:curriculum'),
      subtitle: t('menu:subCurriculum'),
      imageAlt: t('home:products.curriculumTitle'),
      onClickHandler: () => handleItemClick(MenuCategoryKeys.CURRICULUM),
    },
    [MenuCategoryKeys.TEST_PREP]: {
      image: 'icons/products/test_prep_badge.png',
      imageHeight: 48,
      imageWidth: 48,
      key: MenuCategoryKeys.TEST_PREP,
      title: t('home:products.testPrepArena'),
      subtitle: t('menu:subTestPrep'),
      url: ROUTES.TEST_START,
      isBetaFeature: true,
      imageAlt: t('imageAlt:icons.products.testPrep'),
      onClickHandler: () => handleItemClick(MenuCategoryKeys.TEST_PREP),
    },
    [MenuCategoryKeys.TOD]: {
      image: 'icons/products/mentor-on-demand-v3.png',
      imageHeight: 31,
      imageWidth: 46,
      key: MenuCategoryKeys.TOD,
      title: t('home:products.tutorOnDemandTitle'),
      subtitle: t('menu:subTutorOnDemand'),
      url: ROUTES.TUTOR_ON_DEMAND,
      imageAlt: t('imageAlt:icons.products.tutorOnDemand'),
      onClickHandler: () => handleItemClick(MenuCategoryKeys.TOD),
    },
    [MenuCategoryKeys.ENCYCLOPEDIA]: {
      image: 'icons/products/encyclopedia-v4.png',
      imageHeight: 36,
      imageWidth: 46,
      key: MenuCategoryKeys.ENCYCLOPEDIA,
      title: t('home:products.encyclopediaTitle'),
      url: ROUTES.ENCYCLOPEDIA,
      imageAlt: t('imageAlt:icons.products.encyclopedia'),
      onClickHandler: () => handleItemClick(MenuCategoryKeys.ENCYCLOPEDIA),
    },
    [MenuCategoryKeys.STUDY_TIPS]: {
      image: 'icons/products/study-tips-v3.png',
      imageHeight: 42,
      imageWidth: 46,
      key: MenuCategoryKeys.STUDY_TIPS,
      title: t('home:products.studyTipsTitle'),
      url: ROUTES.STUDY_TIPS,
      imageAlt: t('imageAlt:icons.products.studyTips'),
      onClickHandler: () => handleItemClick(MenuCategoryKeys.STUDY_TIPS),
    },
    [MenuCategoryKeys.PEN_N_PAPER_EXERCISE]: {
      image: 'icons/products/math-essentials-v3.png',
      imageHeight: 36,
      imageWidth: 46,
      key: MenuCategoryKeys.PEN_N_PAPER_EXERCISE,
      title: t('menu:penNPaperExercise'),
      url: ROUTES.DRILL,
      onClickHandler: () => handleItemClick(MenuCategoryKeys.PEN_N_PAPER_EXERCISE),
    },
    [MenuCategoryKeys.EXCEL_GEOGEBRA]: {
      image: 'icons/products/digital-tools-v3.png',
      imageHeight: 36,
      imageWidth: 46,
      key: MenuCategoryKeys.EXCEL_GEOGEBRA,
      title: t('menu:excelOrGeoGebra'),
      url: ROUTES.DIGITAL_TOOL,
      onClickHandler: () => handleItemClick(MenuCategoryKeys.EXCEL_GEOGEBRA),
    },
    [MenuCategoryKeys.EXAMS]: {
      image: 'icons/products/exams.png',
      imageHeight: 208,
      imageWidth: 229,
      key: MenuCategoryKeys.EXAMS,
      title: t('home:products.examsTitle'),
      url: ROUTES.EXAMS,
      imageAlt: t('imageAlt:icons.products.exams'),
      onClickHandler: () => handleItemClick(MenuCategoryKeys.EXAMS),
    },
    [MenuCategoryKeys.MULT_MASTER]: {
      image: 'gamification/games/multi-master.png',
      imageHeight: 36,
      imageWidth: 46,
      hasWideLogo: true,
      key: MenuCategoryKeys.MULT_MASTER,
      title: t('menu:multiplicationMaster'),
      url: authenticated ? ROUTES.MULT_GAME : ROUTES.MULT_GAME_LOGIN,
      imageAlt: t('menu:multiplicationMaster'),
      onClickHandler: () => handleItemClick(MenuCategoryKeys.MULT_MASTER),
    },
    [MenuCategoryKeys.JUNIOR_MATH]: {
      image: 'gamification/games/junior-math.png',
      imageClassName: 'h-11 w-auto',
      imageHeight: 43,
      imageWidth: 46,
      hasWideLogo: true,
      key: MenuCategoryKeys.JUNIOR_MATH,
      title: t('menu:juniorMath'),
      url: authenticated ? ROUTES.MATH_VAULT : ROUTES.JUNIOR_MATH_LOGIN,
      imageAlt: t('menu:juniorMath'),
      onClickHandler: () => handleItemClick(MenuCategoryKeys.JUNIOR_MATH),
    },
    [MenuCategoryKeys.TREASURE_TRAIL]: {
      image: 'gamification/games/treasure-trail.png',
      imageHeight: 28,
      imageWidth: 46,
      hasWideLogo: true,
      key: MenuCategoryKeys.TREASURE_TRAIL,
      title: t('menu:treasureTrail'),
      url: authenticated ? ROUTES.TREASURETRAIL : ROUTES.TREASURETRAIL_LOGIN,
      imageAlt: t('menu:treasureTrail'),
      onClickHandler: () => handleItemClick(MenuCategoryKeys.TREASURE_TRAIL),
    },
    [MenuCategoryKeys.WIND_SURFER]: {
      image: 'gamification/games/wind-surfer.png',
      imageHeight: 25,
      imageWidth: 46,
      hasWideLogo: true,
      key: MenuCategoryKeys.WIND_SURFER,
      title: t('menu:windSurfer'),
      url: authenticated ? ROUTES.WINDSURFER : ROUTES.WINDSURFER_LOGIN,
      imageAlt: t('menu:windSurfer'),
      onClickHandler: () => handleItemClick(MenuCategoryKeys.WIND_SURFER),
    },
    [MenuCategoryKeys.STACK_N_LOAD]: {
      image: 'gamification/games/stack-n-load.png',
      imageHeight: 36,
      imageWidth: 46,
      hasWideLogo: true,
      key: MenuCategoryKeys.STACK_N_LOAD,
      title: t('menu:stackNLoad'),
      url: authenticated ? ROUTES.STACKNLOAD : ROUTES.STACKNLOAD_LOGIN,
      imageAlt: t('menu:stackNLoad'),
      onClickHandler: () => handleItemClick(MenuCategoryKeys.STACK_N_LOAD),
    },
    [MenuCategoryKeys.MASTERY_CERTIFICATES]: {
      image: 'curriculum/certificate/mastery-certificate-statistics-and-probability_v4.png',
      imageHeight: 300,
      imageWidth: 300,
      key: MenuCategoryKeys.MASTERY_CERTIFICATES,
      title: t('menu:masteryCertificates'),
      imageAlt: t('menu:masteryCertificates'),
      url: ROUTES.ACHIEVEMENTS,
      onClickHandler: () => handleItemClick(MenuCategoryKeys.MASTERY_CERTIFICATES),
    },
    [MenuCategoryKeys.STUDENT_PANEL]: {
      image: 'icons/student-panel-icon.png',
      imageHeight: 38,
      imageWidth: 46,
      key: MenuCategoryKeys.STUDENT_PANEL,
      title: t('menu:studentPanel'),
      subtitle: t('menu:subStudentPanel'),
      imageAlt: t('menu:studentPanel'),
      url: ROUTES.STUDENT_PANEL,
      onClickHandler: () => handleItemClick(MenuCategoryKeys.STUDENT_PANEL),
    },
    [MenuCategoryKeys.TEACHER_PANEL]: {
      // Borrowed from the mentor panel until design supplies a school-teacher icon. Safe to share:
      // Tutor Panel is `!isSchoolPlatform`, so the two entries can never appear in the same menu.
      image: 'icons/mentor-panel-icon.png',
      imageHeight: 24,
      imageWidth: 30,
      key: MenuCategoryKeys.TEACHER_PANEL,
      title: t('menu:teacherDashboard'),
      imageAlt: t('menu:teacherDashboard'),
      url: ROUTES.CLASSROOM_TEACHER_DASHBOARD,
      onClickHandler: () => handleItemClick(MenuCategoryKeys.TEACHER_PANEL),
    },
    [MenuCategoryKeys.HOME]: {
      image: 'icons/home-v2.png',
      imageHeight: 36,
      imageWidth: 46,
      key: MenuCategoryKeys.HOME,
      title: t('menu:dashboard'),
      imageAlt: t('imageAlt:icons.dashboard'),
      url: ROUTES.DASHBOARD,
      onClickHandler: () => handleItemClick(MenuCategoryKeys.HOME),
    },
    [MenuCategoryKeys.PROGRESS]: {
      image: 'icons/stats-panel.png',
      imageHeight: 36,
      imageWidth: 46,
      key: MenuCategoryKeys.PROGRESS,
      title: t('menu:myStatistics'),
      imageAlt: t('imageAlt:icons.statistics'),
      url: ROUTES.STATISTICS,
      onClickHandler: () => handleItemClick(MenuCategoryKeys.PROGRESS),
    },
    [MenuCategoryKeys.TUTOR_PANEL]: {
      image: 'icons/mentor-panel-icon.png',
      imageHeight: 24,
      imageWidth: 30,
      key: MenuCategoryKeys.TUTOR_PANEL,
      title: t('menu:privateTutoring'),
      imageAlt: t('menu:privateTutoring'),
      url: ROUTES.TUTOR_PANEL,
      onClickHandler: () => handleItemClick(MenuCategoryKeys.TUTOR_PANEL),
    },
    [MenuCategoryKeys.ADMIN_PANEL]: {
      image: 'icons/admin-panel-icon.png',
      imageHeight: 24,
      imageWidth: 30,
      key: MenuCategoryKeys.ADMIN_PANEL,
      title: t('menu:adminPanel'),
      imageAlt: t('menu:adminPanel'),
      url: ROUTES.ADMIN_PANEL,
      onClickHandler: () => handleItemClick(MenuCategoryKeys.ADMIN_PANEL),
    },
  }
}
